import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Box, useTheme, useMediaQuery, Badge } from '@mui/material';
import { HouseIcon, MagnifyingGlassIcon, PlusIcon, PopcornIcon } from '@phosphor-icons/react';
import { User } from 'firebase/auth';
import { useUserProfileCache } from '../../contexts/UserProfileCacheContext';
import { useUserFollow } from '../../contexts/UserFollowContext';
import InitialsAvatar from '../widgets/InitialsAvatar';
import { getLastCrewOpenedAt } from '../pages/CrewPage';
import { hapticLight } from '../../utils/nativeHaptics';

interface FloatingBottomNavProps {
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
  user?: User;
  getUserInitials?: (displayName?: string | null) => string;
  getInitialsBackgroundColor?: (displayName?: string | null) => string;
  /** Called when the dedicated "+" circle is tapped. */
  onAddPress?: () => void;
}

const FloatingBottomNav: React.FC<FloatingBottomNavProps> = ({
  value,
  onChange,
  user,
  onAddPress,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { getUserProfile } = useUserProfileCache();
  const { followingActivity } = useUserFollow();

  // Sliding indicator state
  const navContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });

  // Crew dot lives on Home now (Home = Crew feed)
  const [hasNewCrew, setHasNewCrew] = useState(false);
  useEffect(() => {
    const last = getLastCrewOpenedAt();
    const hasNew = followingActivity.some(a => {
      let ts = 0;
      if (typeof a.createdAt === 'string') {
        ts = new Date(a.createdAt).getTime();
      } else if ((a.createdAt as any)?.toMillis) {
        ts = (a.createdAt as any).toMillis();
      } else if ((a.createdAt as any)?.seconds) {
        ts = (a.createdAt as any).seconds * 1000;
      }
      return ts > last;
    });
    setHasNewCrew(hasNew);
  }, [followingActivity]);

  const userProfile = user?.uid ? getUserProfile(user.uid) : null;
  const userProfileData = useMemo(() => {
    if (!user?.uid) return { avatar: undefined, displayName: 'User' };
    return {
      avatar: userProfile?.photoURL || user.photoURL || undefined,
      displayName: userProfile?.displayName || user.displayName || 'User',
    };
  }, [user?.uid, user?.photoURL, user?.displayName, userProfile?.photoURL, userProfile?.displayName]);

  const navItems: Array<{
    icon: React.ComponentType<any> | null;
    value: number;
    type: 'icon' | 'avatar';
    testId: string;
  }> = [
    { icon: HouseIcon, value: 0, type: 'icon', testId: 'home-tab' },
    { icon: PopcornIcon, value: 1, type: 'icon', testId: 'trending-tab' },
    { icon: MagnifyingGlassIcon, value: 2, type: 'icon', testId: 'search-tab' },
    { icon: null, value: 4, type: 'avatar', testId: 'profile-tab' },
  ];

  // Update the sliding indicator whenever the selected tab changes
  useEffect(() => {
    const selectedIdx = value === -1 ? -1 : navItems.findIndex(item => item.value === value);

    if (selectedIdx === -1) {
      setIndicatorStyle(prev => ({ ...prev, opacity: 0 }));
      return;
    }

    const containerEl = navContainerRef.current;
    const itemEl = itemRefs.current[selectedIdx];
    if (!containerEl || !itemEl) return;

    const containerRect = containerEl.getBoundingClientRect();
    const itemRect = itemEl.getBoundingClientRect();

    setIndicatorStyle({
      left: itemRect.left - containerRect.left,
      width: itemRect.width,
      opacity: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleNavClick = (newValue: number) => {
    if (newValue !== value) void hapticLight();
    onChange({} as React.SyntheticEvent, newValue);
  };

  const handleAddClick = () => {
    void hapticLight();
    onAddPress?.();
  };

  if (!isMobile) return null;

  const isDark = theme.palette.mode === 'dark';

  // Shared liquid-glass styling so the pill and circle button match visually.
  const glassSx = {
    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.75)' : 'rgba(255, 255, 255, 0.55)',
    backgroundImage: isDark
      ? 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 45%, rgba(255,255,255,0.08) 100%)'
      : 'linear-gradient(135deg, rgba(255,255,255,0.65) 0%, rgba(255,255,255,0.2) 45%, rgba(255,255,255,0.45) 100%)',
    backdropFilter: 'blur(5px)',
    WebkitBackdropFilter: 'blur(5px)',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.18)' : 'rgba(255, 255, 255, 0.55)'}`,
    boxShadow: isDark
      ? '0 10px 36px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.18), inset 0 -1px 0 rgba(0, 0, 0, 0.3)'
      : '0 10px 36px rgba(0, 0, 0, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.85), inset 0 -1px 0 rgba(0, 0, 0, 0.06)',
  };

  return (
    <>
      {/* Transparent strip below the pill that blocks accidental taps in the gap */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: 'calc(var(--safe-area-inset-bottom) + 10px)',
          zIndex: 1199,
          pointerEvents: 'auto',
          background: 'transparent',
          touchAction: 'none',
        }}
      />

      {/* Outer flex container: pill (left) + circular "+" button (right) */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1200,
          pb: 'var(--safe-area-inset-bottom)',
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.75, sm: 1.25 },
          // Never overflow viewport — keeps layout intact down to 320px
          maxWidth: 'calc(100vw - 16px)',
        }}
      >
        {/* Pill: Home / Trending / Search / Avatar */}
        <Box
          ref={navContainerRef}
          data-testid="bottom-nav"
          sx={{
            position: 'relative',
            borderRadius: '48px',
            px: { xs: 0.5, sm: 1 },
            py: { xs: 1, sm: 1.25 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 'clamp(0px, 1vw, 8px)', sm: '8px' },
            ...glassSx,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              background: isDark
                ? 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 38%)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 45%)',
              mixBlendMode: 'overlay',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              background: isDark
                ? 'radial-gradient(120% 60% at 50% 100%, rgba(124, 105, 250, 0.12) 0%, rgba(0,0,0,0) 60%)'
                : 'radial-gradient(120% 60% at 50% 100%, rgba(54, 52, 163, 0.08) 0%, rgba(255,255,255,0) 60%)',
            },
            '& > *': { position: 'relative', zIndex: 1 },
          }}
        >
          {/* Sliding selection indicator */}
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              left: indicatorStyle.left,
              width: indicatorStyle.width,
              height: 'calc(100% - 12px)',
              bgcolor: isDark ? 'rgba(146, 129, 255, 0.31)' : 'rgba(54, 52, 163, 0.1)',
              borderRadius: 100,
              opacity: indicatorStyle.opacity,
              pointerEvents: 'none',
              zIndex: 0,
              transition: 'left 0.28s cubic-bezier(0.4, 0, 0.2, 1), width 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.18s ease',
            }}
          />

          {navItems.map((item, idx) => {
            const isSelected = value !== -1 && value === item.value;
            const Icon = item.icon;

            return (
              <Box
                key={item.value}
                ref={(el: HTMLDivElement | null) => { itemRefs.current[idx] = el; }}
                data-testid={item.testId}
                onClick={() => handleNavClick(item.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: { xs: 'clamp(64px, 18vw, 96px)', sm: 96 },
                  height: { xs: 52, sm: 48 },
                  cursor: 'pointer',
                  borderRadius: 100,
                  transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  bgcolor: 'transparent',
                  '&:hover': isSelected ? {} : {
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                    transform: 'scale(1.08)',
                  },
                  '&:active': { transform: 'scale(0.95)' },
                }}
              >
                {item.type === 'avatar' ? (
                  <InitialsAvatar
                    src={userProfileData.avatar}
                    fallbackSrc={user?.photoURL || undefined}
                    name={userProfileData.displayName}
                    size={isSelected ? 32 : 30}
                    sx={{
                      transition: 'all 0.2s ease',
                      border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : `2px solid ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}`,
                      transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                      boxShadow: isSelected
                        ? isDark
                          ? '0 0 0 2px rgba(125, 122, 255, 0.3)'
                          : '0 0 0 2px rgba(54, 52, 163, 0.3)'
                        : 'none',
                    }}
                  />
                ) : (
                  Icon && (
                    item.value === 0 ? (
                      <Badge
                        variant="dot"
                        invisible={!hasNewCrew}
                        sx={{
                          '& .MuiBadge-dot': {
                            backgroundColor: '#FF5722',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            border: `2px solid ${isDark ? '#2C2C2E' : '#FFFFFF'}`,
                          },
                        }}
                      >
                        <Icon
                          size={isMobile ? 28 : 26}
                          color={isSelected ? theme.palette.common.white : isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(60, 60, 67, 0.85)'}
                          weight={isSelected ? 'duotone' : 'regular'}
                        />
                      </Badge>
                    ) : (
                      <Icon
                        size={isMobile ? 28 : 26}
                        color={isSelected ? theme.palette.common.white : isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(60, 60, 67, 0.85)'}
                        weight={isSelected ? 'duotone' : 'regular'}
                      />
                    )
                  )
                )}
              </Box>
            );
          })}
        </Box>

        {/* Separate circular "+" button — brand tint to stand out */}
        <Box
          data-testid="bottom-nav-add"
          onClick={handleAddClick}
          sx={{
            width: { xs: 'clamp(56px, 15vw, 68px)', sm: 60 },
            height: { xs: 'clamp(56px, 15vw, 68px)', sm: 60 },
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            position: 'relative',
            backgroundColor: 'rgba(211, 15, 69, 0.8)',
            backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 50%)',
            backdropFilter: 'blur(5px)',
            WebkitBackdropFilter: 'blur(5px)',
            border: '1px solid rgba(255,255,255,0.22)',
            boxShadow: '0 10px 36px rgba(211,15,69,0.4), inset 0 1px 0 rgba(255,255,255,0.25)',
            transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'scale(1.06)',
            },
            '&:active': { transform: 'scale(0.94)' },
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 50%)',
              mixBlendMode: 'overlay',
            },
            '& > *': { position: 'relative', zIndex: 1 },
          }}
        >
          <PlusIcon
            size={isMobile ? 32 : 30}
            color="rgba(255, 255, 255, 0.95)"
          />
        </Box>
      </Box>
    </>
  );
};

export default FloatingBottomNav;
